"use client";

import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/Arrow";
import { Grid } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import {
  handleGenerateBrandUrl,
  handleGenerateProductLinkProps,
} from "@faire/retailer/components/Carousel/utils";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { MarketplaceProduct } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { ILeanProductWithBrandTile } from "@faire/retailer/lib/ILeanProductWithBrandTile";

import { BaseCarouselProps, getTokensForViewEvent } from "../CarouselBase";

export const VerticalExpandingTiles: React.FC<BaseCarouselProps> = () => {
  const { strictLocalize } = useStrictLocalization();
  const location = useLocation();
  const [currentExpansion, setCurrentExpansion] = useState(0);
  const { layoutElement, requestId, onItemClick, onVisible, onCTAClick } =
    React.useContext(Context);

  const rowsPerState = useResponsiveValue({
    mobileAndAbove: 4,
    tabletAndAbove: 2,
  });

  const itemsPerRow = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 4,
    desktopAndAbove: 5,
  });

  const itemsPerState = rowsPerState * itemsPerRow;
  const items = layoutElement.layout_element_data.filter(isNotUndefined);
  const maxExpansions = Math.min(
    Math.floor((items.length - 1) / itemsPerState),
    2
  );

  const handleOnProductClick =
    (product: ILeanProductTile.ILeanProduct | undefined) => () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        product?.token ?? ""
      );
    };

  const handleOnBrandClick =
    (brand: ILeanProductTile.IBrand | undefined) => () => {
      onItemClick?.(
        layoutElement.on_click_event_name ?? "",
        requestId,
        brand?.token ?? ""
      );
    };

  const handleComponentVisible = useCallback(
    (start: number) => {
      const items = getTokensForViewEvent(layoutElement, strictLocalize)?.slice(
        start,
        start + itemsPerState
      );
      if (layoutElement.on_click_event_name) {
        onVisible?.(
          layoutElement.on_click_event_name,
          requestId,
          items?.join(" ") ?? ""
        );
      }
    },
    [itemsPerState, layoutElement, onVisible, requestId, strictLocalize]
  );

  const handleClickSeeMore = useCallback(() => {
    onCTAClick?.(layoutElement.on_click_event_name ?? "", requestId);
    handleComponentVisible(itemsPerState * (currentExpansion + 1));
    setCurrentExpansion(currentExpansion + 1);
  }, [
    currentExpansion,
    handleComponentVisible,
    itemsPerState,
    layoutElement.on_click_event_name,
    onCTAClick,
    requestId,
  ]);

  const renderItem = (data: ILayoutElementData) => {
    const tile = ILeanProductWithBrandTile.fromLayoutElementData(
      data,
      strictLocalize
    );
    return tile ? (
      <MarketplaceProduct
        key={tile.product?.product?.token}
        recordAdImpression
        productTile={tile.product}
        brand={tile.brand}
        generateProductLinkProps={handleGenerateProductLinkProps(
          tile.product,
          location
        )}
        generateBrandUrl={handleGenerateBrandUrl(
          tile.brand?.token,
          tile.product?.product?.token
        )}
        onClick={handleOnProductClick(tile.product?.product)}
        onBrandClick={handleOnBrandClick(tile.brand)}
        showVerticalImagery={layoutElement.show_vertical_imagery}
      />
    ) : null;
  };

  const viewRef = useDoActionWhenVisible(() =>
    handleComponentVisible(itemsPerState * currentExpansion)
  );

  return items.length >= 5 ? (
    <GridWrapper ref={viewRef}>
      <Grid
        columns={itemsPerRow}
        columnGap={{ mobileAndAbove: "1x", desktopAndAbove: "2x" }}
        rowGap={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }}
        columnTemplate={(columns: number) =>
          `repeat(${columns}, minmax(0,1fr));`
        }
      >
        {items.slice(0, (currentExpansion + 1) * itemsPerState).map(renderItem)}
      </Grid>
      {currentExpansion < maxExpansions ? (
        <ButtonContentWrapper>
          <StyledButton variant="tertiary" onClick={handleClickSeeMore}>
            <StrictLocalMsg
              defaultMessage="Show more"
              description={{
                text: "Show grid items that are more than certain number of rows that was previously made hidden",
              }}
            />
            <Spacer size="1x" />
            <Arrow direction="down" fontSize="12px" />
          </StyledButton>
        </ButtonContentWrapper>
      ) : null}
    </GridWrapper>
  ) : null;
};

const ButtonContentWrapper = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  padding-top: ${getSpacing("5x")};
  ${responsiveMediaQueries.mobile} {
    padding-top: ${getSpacing("2x")};
  }
`;

const StyledButton = styled(Button)`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const GridWrapper = styled.div`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
  }
`;
